import config from '../config';

// Short-link domains must be ones only Lashbrook can create links on, since the redirect target isn't checked.
const SHORT_LINK_HOSTS = ['lashbrook.link'];
const PRODUCTION_SITE_HOSTS = [
  'lashbrookdesigns.com',
  'www.lashbrookdesigns.com',
  'ringdesignersstudio.com',
  'www.ringdesignersstudio.com',
];
const STAGING_SITE_HOSTS = ['staging.lashbrookdesigns.com', 'staging2.lashbrookdesigns.com', 'staging.ringdesignersstudio.com'];
const LOCAL_HOSTS = ['localhost', '127.0.0.1'];

interface QrScannerSettings {
  qrScannerEnabled?: unknown;
  qrScannerParentOrigins?: unknown;
}

const settings = config as QrScannerSettings;

// Shopify stores serve the customizer at the same /apps/<subpath> path on both their myshopify.com and primary domains.
export const isShopifyHost = (host: string) => host.toLowerCase().endsWith('.myshopify.com');

export const isLocalDev = () => LOCAL_HOSTS.includes(window.location.hostname);

// Sites that serve the customizer, so a local build can open their codes in-app (short links have no meaningful path).
export const isLashbrookSiteHost = (host: string) =>
  [...PRODUCTION_SITE_HOSTS, ...STAGING_SITE_HOSTS].includes(host.toLowerCase());

// The customizer is usually served from (or embedded in) a client's domain, so the current site and any page framing it
// are approved alongside Lashbrook's own hosts. ancestorOrigins isn't available in every browser, so it's best-effort.
// Staging is only approved from localhost or staging itself so production kiosks can never be sent to a staging QR code.
export const qrScannerAllowedHosts = () => {
  const host = window.location.hostname;
  const ancestorHosts = Array.from(window.location.ancestorOrigins ?? []).map(origin => new URL(origin).hostname);
  const stagingHosts = isLocalDev() || STAGING_SITE_HOSTS.includes(host) ? STAGING_SITE_HOSTS : [];
  const hosts = [...SHORT_LINK_HOSTS, ...PRODUCTION_SITE_HOSTS, ...stagingHosts, host, ...ancestorHosts];
  return Array.from(new Set(hosts.map(item => item.toLowerCase())));
};

// CloudFront environment settings arrive as strings, so lists may be comma-separated.
const settingList = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string') return value.split(',').map(item => item.trim()).filter(Boolean);
  return [];
};

export const qrScannerEnabled = () => settings.qrScannerEnabled !== false && settings.qrScannerEnabled !== 'false';

export const qrScannerParentOrigins = () => settingList(settings.qrScannerParentOrigins);

export const approvedScanUrl = (text: string, allowedHosts: string[], allowedPaths: RegExp[] = []) => {
  let url: URL;
  try {
    url = new URL(text.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (url.username || url.password) return null;
  if (!allowedHosts.includes(url.hostname.toLowerCase())) return null;
  if (allowedPaths.length && !allowedPaths.some(pattern => pattern.test(url.pathname))) return null;
  return url;
};
